/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState } from 'react';
import SelectBox from '@components/form/SelectBox';

import useApi from '@/hooks/useApi';
import { MsCatalog } from '@api/ms-catalog';
import PropTypes from 'prop-types';
import { isVn } from '@global/helpers';

const propTypes = {
  editable: PropTypes.bool,
  isRequired: PropTypes.bool,
  multiple: PropTypes.bool,
  hasError: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  customFilter: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  allBrand: PropTypes.bool,
  onChange: PropTypes.func,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
    PropTypes.object,
  ]),
  hideLabel: PropTypes.bool,
};

const defaultProps = {
  editable: true,
  isRequired: false,
  multiple: false,
  hasError: false,
  onChange: () => {},
  allBrand: false,
  customFilter: {},
  value: '',
  hideLabel: false,
};

const limit = 10;

const BrandSearch = (props) => {
  const {
    editable = true,
    isRequired = false,
    multiple = false,
    hasError = false,
    allBrand = false,
    customFilter = {},
    value = '',
    hideLabel = false,
  } = props;

  const [loading, setLoading] = useState({
    initial: false,
    loadMore: false,
  });
  const [options, setOptions] = useState([]);
  const [skip, setSkip] = useState(0);

  const getBrands = useApi(MsCatalog.getBrands);

  const onSearch = async (val, newSkip = 0) => {
    if (newSkip) {
      setLoading({ ...loading, loadMore: true });
    } else {
      setLoading({ ...loading, initial: true });
    }
    try {
      let filter = {
        ...(isVn()
          ? { is_active_in_b2b_vn: true }
          : { is_active_in_b2b: true }),
      };
      if (customFilter && Object.keys(customFilter).length) {
        filter = {
          ...filter,
          ...customFilter,
        };
      }
      if (val) {
        filter.name = { $regex: val, $options: 'i' };
      }
      if (!val && filter.name) {
        delete filter.name;
      }
      const response = await getBrands.request({
        params: {
          filter: filter,
          skip: newSkip,
          limit: limit,
          sort: JSON.stringify({ name: 1 }),
        },
      });
      if (response.status == 200 && response?.data?.data?.length) {
        const result = response.data.data.map((item) => {
          if (item._id) {
            item.id = item._id;
          }
          return item;
        });
        if (allBrand && newSkip == 0) {
          result.unshift({ id: 'all', name: 'All Brand' });
        }
        if (newSkip) {
          setOptions(options.concat(result));
        } else {
          setOptions(result);
        }
      } else {
        if (!newSkip) {
          setOptions([]);
        }
      }

      if (newSkip) {
        setLoading({ ...loading, loadMore: false });
      } else {
        setLoading({ ...loading, initial: false });
      }
    } catch (error) {
      if (newSkip) {
        setLoading({ ...loading, loadMore: false });
      } else {
        setLoading({ ...loading, initial: false });
      }
    }
  };

  return (
    <SelectBox
      label="Brand"
      options={options}
      value={value}
      hideLabel={hideLabel}
      placeholder={
        loading.initial || loading.loadMore ? 'Loading...' : 'Select Brand'
      }
      trackBy="id"
      labelKey="name"
      isRequired={isRequired}
      multiple={multiple}
      editable={editable && !loading.initial}
      hasError={hasError}
      loading={loading.initial}
      loadingCallback={loading.loadMore}
      withCallback={true}
      withLoadMore={true}
      loadMoreEmpty={skip >= options.length}
      searchCallback={(val) => {
        setSkip(0);
        onSearch(val);
      }}
      onLoadMore={(val) => {
        const newSkip = skip + 10;
        setSkip(newSkip);
        onSearch(val, newSkip);
      }}
      onChange={(val) => {
        let finalValue = val;
        if (multiple) {
          if (val?.length) {
            const allIndex = val.findIndex((a) => a?.id == 'all');
            if (allIndex >= 0 && val.length >= 1) {
              if (allIndex == 0) {
                val.splice(allIndex, 1);
                finalValue = val;
              } else {
                finalValue = [{ id: 'all', name: 'All Brand' }];
              }
            }
          }
        }
        props.onChange(finalValue);
      }}
      onReady={() => {
        setSkip(0);
        onSearch();
      }}
    />
  );
};

BrandSearch.propTypes = propTypes;
BrandSearch.defaultProps = defaultProps;

export default BrandSearch;
